import { useCallback, useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg, brl, fmtDate } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { ConfirmCodeDialog } from "../components/ConfirmCodeDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const EMPTY = { amount: "", destination_type: "pix", destination: "", description: "" };

export default function Payouts() {
  const api = useApi();
  const [rows, setRows] = useState([]);
  const [balance, setBalance] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(() => {
    api.get("/admin/transactions", { params: { type: "payout" } }).then((r) => setRows(r.data)).catch(() => {});
    api.get("/admin/stats").then((r) => setBalance(r.data.balance)).catch(() => {});
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const confirm = async (code) => {
    try {
      if (action.kind === "create") {
        await api.post("/admin/payouts", { ...f, amount: parseFloat(String(f.amount).replace(",", ".")), description: f.description || "Saque", code });
        setF(EMPTY);
        toast.success("Saque solicitado. Aprove após enviar o pagamento.");
      } else if (action.kind === "approve") {
        await api.post(`/admin/payouts/${action.tx.id}/approve`, { code });
        toast.success("Saque marcado como pago");
      } else {
        await api.post(`/admin/payouts/${action.tx.id}/reject`, { code, reason });
        toast.success("Saque recusado");
      }
      setAction(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const dialogText = {
    create: ["Confirmar solicitação de saque", `Saque de ${brl(parseFloat(String(f.amount).replace(",", ".")) || 0)} para ${f.destination}.`],
    approve: ["Aprovar saque", `Confirme que você já enviou ${brl(action?.tx?.amount)} para ${action?.tx?.destination}.`],
    reject: ["Recusar saque", "O valor volta para o saldo disponível."],
  }[action?.kind || "create"];

  return (
    <div className="space-y-8 fade-up">
      <div><p className="caption">Saídas</p><h1 className="font-heading text-4xl font-extrabold sm:text-5xl">Saques e pagamentos</h1></div>
      <form className="card space-y-4" onSubmit={(e) => { e.preventDefault(); setAction({ kind: "create" }); }} data-testid="new-payout-form">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-heading text-lg font-bold">Novo saque / pagamento</h2>
          <span className="text-sm text-slate-400">Disponível: <b className="font-mono text-emerald-400" data-testid="payout-available-balance">{brl(balance?.available)}</b></span>
        </div>
        <div className="grid gap-4 sm:grid-cols-4">
          <div><Label className="caption">Valor (R$)</Label><Input className="field mt-1" required inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="payout-amount-input" /></div>
          <div><Label className="caption">Tipo</Label>
            <select className="field mt-1 h-9 w-full rounded-md px-3 text-sm" value={f.destination_type} onChange={(e) => setF({ ...f, destination_type: e.target.value })} data-testid="payout-type-select">
              <option value="pix">Chave Pix</option><option value="bank">Conta bancária</option>
            </select>
          </div>
          <div className="sm:col-span-2"><Label className="caption">{f.destination_type === "pix" ? "Chave Pix" : "Banco / agência / conta / titular"}</Label><Input className="field mt-1" required minLength={3} maxLength={200} value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} data-testid="payout-destination-input" /></div>
        </div>
        <Input className="field" placeholder="Descrição (opcional)" maxLength={140} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="payout-description-input" />
        <Button type="submit" className="btn-primary" data-testid="request-payout-button">Solicitar saque (taxa 0%)</Button>
      </form>
      <div className="card">
        <h2 className="mb-4 font-heading text-lg font-bold">Solicitações</h2>
        <div className="overflow-x-auto">
          <table className="tx-table" data-testid="payouts-table">
            <thead><tr><th>Destino</th><th>Origem</th><th>Valor</th><th>Status</th><th>Data</th><th></th></tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-500">Nenhum saque.</td></tr>}
              {rows.map((t) => (
                <tr key={t.id} data-testid={`payout-row-${t.id}`}>
                  <td className="max-w-[260px] truncate"><span className="mr-1 text-xs uppercase text-slate-500">{t.destination_type}</span>{t.destination}</td>
                  <td className="text-xs uppercase text-slate-400">{t.source}</td>
                  <td className="font-mono">{brl(t.amount)}</td>
                  <td><StatusBadge status={t.status} /></td>
                  <td className="whitespace-nowrap text-xs text-slate-400">{fmtDate(t.created_at)}</td>
                  <td className="whitespace-nowrap text-right">
                    {t.status === "pending_approval" && (
                      <>
                        <Button size="sm" className="btn-primary mr-1" onClick={() => setAction({ kind: "approve", tx: t })} data-testid={`approve-payout-button-${t.id}`}><Check size={14} /> Aprovar</Button>
                        <Button size="sm" variant="ghost" className="text-rose-400" onClick={() => setAction({ kind: "reject", tx: t })} data-testid={`reject-payout-button-${t.id}`}><X size={14} /> Recusar</Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <ConfirmCodeDialog open={!!action} onOpenChange={(o) => !o && setAction(null)} title={dialogText[0]} description={dialogText[1]} onConfirm={confirm}>
        {action?.kind === "reject" && <Textarea className="field" placeholder="Motivo (opcional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reject-reason-input" />}
      </ConfirmCodeDialog>
    </div>
  );
}
